import React, { useContext, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Pagination, Select, Table, Tooltip } from "antd";
import DebounceInput from "../../../common/DebounceInput";
import useAuthContext from "../../../hooks/useAuthContext";
import { SourceProviderContext } from "../../../context/sourceProvider";
import { ALL_SOURCES } from "../../../apis/admin";
import {
  useAdminBatches,
  useAdminSources,
  useAdminUsers,
} from "../../../hooks/useAdmin";
import AdminDownloadInputModal from "../AdminDownloadInputModal";
import {
  canDownloadBatchOutput,
  FilterField,
  formatUserLabel,
  INTERNAL_OPTIONS,
  isBstBatch,
  queryErrorMessage,
  storedIsInternal,
} from "../helpers";
import "./style.scss";

const AdminBatchList = () => {
  const {
    user: { isInternal },
  } = useAuthContext();
  const { sourceData } = useContext(SourceProviderContext);
  const [sourceId, setSourceId] = useState(sourceData?._id);
  const [isInternalFilter, setIsInternalFilter] = useState("false");
  const [userId, setUserId] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [batchName, setBatchName] = useState("");
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [downloadTarget, setDownloadTarget] = useState(null);

  useEffect(() => {
    if (!sourceId && sourceData?._id) setSourceId(sourceData._id);
  }, [sourceData?._id, sourceId]);

  const resetPage = () => setPageNum(1);

  const isInternalUser = Boolean(isInternal || storedIsInternal());
  const canQuery = Boolean(isInternalUser && sourceId);
  const { data: sources = [] } = useAdminSources({ enabled: isInternalUser });
  const { data: users = [], isLoading: usersLoading } = useAdminUsers(
    {
      pageNum: 1,
      pageSize: 200,
      search: userSearch,
      sourceId,
      isInternal: isInternalFilter,
    },
    { enabled: canQuery }
  );
  const {
    data: batchPage,
    isLoading: batchesLoading,
    isError: batchesError,
    error: batchesErr,
    refetch: refetchBatches,
  } = useAdminBatches(
    {
      userId,
      pageNum,
      pageSize,
      sourceId,
      batchName,
      isInternal: isInternalFilter,
    },
    { enabled: canQuery }
  );

  const batches = batchPage?.batches || [];
  const totalCount = batchPage?.totalCount || 0;

  if (!isInternalUser) return <Navigate to="/order-list" replace />;

  return (
    <div className="admin_batch_list">
      <div className="admin_batch_list_content">
        <div className="admin_header">
          <h1>Batches (Admin)</h1>
          <p>Client batches by default. Filter by source, user, or batch name.</p>
        </div>
        <div className="admin_filters">
          <FilterField label="Source">
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Current source"
              value={sourceId}
              onChange={(value) => {
                setSourceId(value);
                setUserId(null);
                resetPage();
              }}
              options={[
                { value: ALL_SOURCES, label: "All sources" },
                ...sources.map((source) => ({
                  value: source._id,
                  label: source.name || source.sourceCode,
                })),
              ]}
            />
          </FilterField>
          <FilterField label="Internal user">
            <Select
              value={isInternalFilter}
              onChange={(value) => {
                setIsInternalFilter(value);
                setUserId(null);
                resetPage();
              }}
              options={INTERNAL_OPTIONS}
            />
          </FilterField>
          <FilterField label="Filter by user">
            <Select
              showSearch
              allowClear
              placeholder="All users"
              value={userId}
              filterOption={false}
              onSearch={setUserSearch}
              loading={usersLoading}
              onChange={(value) => {
                setUserId(value || null);
                resetPage();
              }}
              options={users.map((user) => ({
                value: user._id,
                label: formatUserLabel(user),
              }))}
              notFoundContent="No users found"
            />
          </FilterField>
          <FilterField label="Batch name">
            <DebounceInput
              searchName={batchName}
              setSearchName={(value) => {
                setBatchName(value);
                resetPage();
              }}
              placeholder="Search batch name or number"
              allowClear
            />
          </FilterField>
        </div>
        <div className="list_wrapper">
          {batchesError && (
            <p className="admin_error">
              {queryErrorMessage(batchesErr)}{" "}
              <button type="button" className="text_action" onClick={() => refetchBatches()}>
                Retry
              </button>
            </p>
          )}
          <Table
            pagination={false}
            loading={batchesLoading}
            scroll={{ y: "100%" }}
            rowKey="_id"
            dataSource={batches}
            locale={{ emptyText: batchesError ? " " : "No batches found." }}
            columns={[
              {
                title: "Batch No.",
                dataIndex: "batchNumber",
                render: (value) => value || "N/A",
              },
              { title: "Batch Name", dataIndex: "name" },
              {
                title: "Source",
                render: (_, row) => row.source?.name || row.source?.sourceCode || "—",
              },
              {
                title: "User",
                render: (_, row) => formatUserLabel(row.user),
              },
              {
                title: "Stage",
                align: "center",
                render: (_, row) => row.displayStage || row.stage,
              },
              { title: "SKUs", dataIndex: "skuCount", align: "center" },
              {
                title: "Created",
                align: "center",
                render: (_, row) =>
                  row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "—",
              },
              {
                title: "Actions",
                align: "center",
                render: (_, row) => (
                  <div className="admin_actions">
                    <Link to={`/admin/batches/${row._id}`}>View SKUs</Link>
                    <button
                      type="button"
                      className="text_action"
                      onClick={() => setDownloadTarget({ batchId: row._id, kind: "input" })}
                    >
                      Download input
                    </button>
                    {!isBstBatch(row) && (
                      <Tooltip
                        title={
                          canDownloadBatchOutput(row)
                            ? undefined
                            : "Available once the batch is in review."
                        }
                      >
                        <span>
                          <button
                            type="button"
                            className="text_action"
                            disabled={!canDownloadBatchOutput(row)}
                            onClick={() =>
                              setDownloadTarget({ batchId: row._id, kind: "output" })
                            }
                          >
                            Download output
                          </button>
                        </span>
                      </Tooltip>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </div>
        <div className="pagination_wrapper">
          <Pagination
            total={totalCount}
            current={pageNum}
            pageSize={pageSize}
            onChange={(page, size) => {
              setPageNum(page);
              setPageSize(size);
            }}
          />
        </div>
      </div>
      {downloadTarget && (
        <AdminDownloadInputModal
          batchId={downloadTarget.batchId}
          kind={downloadTarget.kind}
          onClose={() => setDownloadTarget(null)}
        />
      )}
    </div>
  );
};

export default AdminBatchList;
